import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  InputAdornment,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { SearchOutlined as SearchIcon, StarRounded as StarIcon } from '@mui/icons-material';

const LIMIT = 50;

// ── Shared sub-components ─────────────────────────────────────────────────────

const ScoreBar = ({ label, value, pct, weight }) => (
  <Box sx={{ mb: 1.5 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {label}{' '}
        {weight != null && (
          <Typography component="span" variant="caption" sx={{ color: 'text.disabled' }}>
            ({Math.round(weight * 100)}%)
          </Typography>
        )}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>{Math.round(value ?? 0)}</Typography>
    </Box>
    <LinearProgress
      variant="determinate"
      value={Math.min(pct ?? 0, 100)}
      sx={{
        height: 8,
        borderRadius: 4,
        backgroundColor: 'rgba(40,75,50,0.1)',
        '& .MuiLinearProgress-bar': { backgroundColor: '#284B32', borderRadius: 4 },
      }}
    />
  </Box>
);

const OverallBadge = ({ score }) => {
  const color = score >= 80 ? '#1b5e20' : score >= 60 ? '#e65100' : '#b71c1c';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
      <Typography sx={{ fontWeight: 800, fontSize: '1.5rem', color, lineHeight: 1 }}>{score}</Typography>
    </Box>
  );
};

// ── Shared sub-components ─────────────────────────────────────────────────────

const StarRating = ({ value }) => (
  <Box sx={{ display: 'flex', gap: 0.25 }}>
    {[1, 2, 3, 4, 5].map(n => (
      <StarIcon key={n} sx={{ fontSize: '1rem', color: n <= value ? '#e6a817' : 'rgba(0,0,0,0.12)' }} />
    ))}
  </Box>
);

// ── Judge detail panel ────────────────────────────────────────────────────────

const JudgeDetail = ({ data }) => {
  const { judge, overall, breakdown, weights, events, directorFeedbacks = [] } = data;

  const efectividad = breakdown.efectividad ?? 0;
  const scoreIndex  = breakdown.scoreIndex ?? 0;
  const hasStars    = breakdown.starsAvg != null;
  const starsScore  = hasStars ? (breakdown.starsAvg / 5) * 100 : null;

  const initials = judge.fullname
    ? judge.fullname.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase()
    : judge.username?.substring(0, 2).toUpperCase();

  return (
    <>
      <Box sx={{ backgroundColor: '#1E3A2F', p: 2.5, borderRadius: '14px 14px 0 0', display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{
          width: 48, height: 48, borderRadius: '50%',
          backgroundColor: 'rgba(255,255,255,0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontWeight: 700, fontSize: '1rem', flexShrink: 0,
        }}>
          {initials}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.2 }}>
            {judge.fullname || judge.username}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.8rem' }}>
            {events.length} eventos · {judge.username}
          </Typography>
        </Box>
        <OverallBadge score={overall} />
      </Box>

      <CardContent sx={{ p: 2.5 }}>
        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2.5 }}>
          <Tooltip title="Rubrics calificados / slots asignados totales">
            <Chip label={`Efectividad: ${efectividad}%`} size="small" variant="outlined" />
          </Tooltip>
          <Tooltip title="Promedio de (calificación / puntos máximos) × 100">
            <Chip label={`Índice punt.: ${scoreIndex}/100`} size="small" variant="outlined" />
          </Tooltip>
          {hasStars && (
            <Tooltip title="Promedio de estrellas del director">
              <Chip label={`★ Director: ${breakdown.starsAvg.toFixed(1)}`} size="small" variant="outlined" />
            </Tooltip>
          )}
        </Stack>

        <Divider sx={{ mb: 2 }} />

        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#284B32' }}>
          Desglose del puntaje overall
        </Typography>
        <ScoreBar label="Efectividad"          value={efectividad} pct={efectividad} weight={null} />
        <ScoreBar label="Índice de puntuación" value={scoreIndex}  pct={scoreIndex}  weight={hasStars ? weights.grades : weights.grades + weights.stars} />
        {hasStars && (
          <ScoreBar label="Estrellas director" value={starsScore}  pct={starsScore}  weight={weights.stars} />
        )}

        {events.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#284B32' }}>
              Desglose por evento
            </Typography>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Evento</TableCell>
                    <TableCell align="center">Efectividad</TableCell>
                    <TableCell align="center">Índice punt.</TableCell>
                    <TableCell align="center">Calif. / Slots</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {events.map(ev => (
                    <TableRow key={ev.eventId} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{ev.eventName}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        {ev.efectividad !== null ? `${ev.efectividad}%` : <Typography variant="caption" color="text.disabled">—</Typography>}
                      </TableCell>
                      <TableCell align="center">
                        {ev.scoreIndex !== null ? `${ev.scoreIndex}/100` : <Typography variant="caption" color="text.disabled">—</Typography>}
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="caption">{ev.gradedCount} / {ev.totalSlots}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Director feedback */}
        {directorFeedbacks.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#284B32' }}>
              Comentarios del director
            </Typography>
            <Stack spacing={1.5}>
              {directorFeedbacks.map((fb, i) => (
                <Box
                  key={i}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    backgroundColor: 'rgba(40,75,50,0.05)',
                    border: '1px solid rgba(40,75,50,0.12)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                    <Box>
                      {fb.rubricName && (
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#284B32' }}>
                          {fb.rubricName}
                        </Typography>
                      )}
                      {fb.eventName && (
                        <Typography variant="caption" sx={{ color: 'text.disabled', ml: fb.rubricName ? 0.75 : 0 }}>
                          · {fb.eventName}
                        </Typography>
                      )}
                    </Box>
                    <StarRating value={fb.stars} />
                  </Box>
                  <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.5 }}>
                    {fb.comment}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </>
        )}
      </CardContent>
    </>
  );
};

// ── Judge list item ───────────────────────────────────────────────────────────

const JudgeListItem = ({ data, selected, onClick }) => {
  const { judge, overall, breakdown } = data;
  const color = overall >= 80 ? '#1b5e20' : overall >= 60 ? '#e65100' : '#b71c1c';
  const initials = judge.fullname
    ? judge.fullname.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase()
    : judge.username?.substring(0, 2).toUpperCase();

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, mb: 1,
        borderRadius: 2, cursor: 'pointer',
        backgroundColor: selected ? '#C1EFD0' : 'background.paper',
        border: selected ? '1.5px solid #284B32' : '1.5px solid transparent',
        transition: 'background-color 0.15s, border-color 0.15s',
        '&:hover': { backgroundColor: selected ? '#C1EFD0' : 'action.hover' },
      }}
    >
      <Box sx={{
        width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
        backgroundColor: selected ? '#284B32' : 'rgba(40,75,50,0.12)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: selected ? '#fff' : '#284B32', fontWeight: 700, fontSize: '0.75rem',
      }}>
        {initials}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2, color: selected ? '#284B32' : 'text.primary' }} noWrap>
          {judge.fullname || judge.username}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled' }}>
          {breakdown.efectividad ?? 0}% ef. · {breakdown.scoreIndex ?? 0}/100
        </Typography>
      </Box>
      <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color, flexShrink: 0 }}>
        {overall}
      </Typography>
    </Box>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────

const PublicJudgePerformancePage = () => {
  const { campId } = useParams();

  const [campName, setCampName]       = useState('');
  const [items, setItems]             = useState([]);
  const [page, setPage]               = useState(1);
  const [hasMore, setHasMore]         = useState(false);
  const [loading, setLoading]         = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch]           = useState('');
  const [selectedJudge, setSelectedJudge] = useState(null);

  const sentinelRef    = useRef(null);
  const searchTimerRef = useRef(null);
  const fetchedRef     = useRef({ search: null, page: null });

  // El nombre del campamento viene ahora en la propia respuesta del reporte.
  // Antes se pedía a /club-invite/camp/:campId y se leía `data.camp.name`, pero ese
  // endpoint devuelve el camp plano, así que el nombre nunca llegaba a mostrarse.

  const fetchPage = useCallback(async (searchVal, pageNum) => {
    if (!campId) return;
    setLoading(true);
    try {
      // Endpoint público: /reports/judge-performance exige permisos desde la Fase 1
      // del RBAC y esta página se comparte por enlace, sin sesión.
      const { data } = await axios.get(
        `${process.env.REACT_APP_REST_API}/reports/public/judge-performance/${campId}`,
        { params: { search: searchVal || undefined, page: pageNum, limit: LIMIT } }
      );
      if (data.campName) setCampName(data.campName);
      setItems(prev => {
        const next = pageNum === 1 ? data.results : [...prev, ...data.results];
        if (pageNum === 1 && next.length > 0) {
          setSelectedJudge(p => p ?? next[0]);
        }
        return next;
      });
      setHasMore(pageNum < data.totalPages);
    } catch (err) {
      console.error('Error fetching judge performance:', err);
    } finally {
      setLoading(false);
    }
  }, [campId]);

  useEffect(() => { setSelectedJudge(null); }, [search]);

  useEffect(() => {
    const ref = fetchedRef.current;
    if (ref.search === search && ref.page === page) return;
    fetchedRef.current = { search, page };
    fetchPage(search, page);
  }, [search, page, fetchPage]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setItems([]);
      setPage(1);
      setSearch(val);
    }, 300);
  };

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && hasMore && !loading) setPage(p => p + 1); },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, loading]);

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f4f1e9', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ backgroundColor: '#1E3A2F', px: { xs: 2, md: 4 }, py: 2.5, flexShrink: 0 }}>
        <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: { xs: '1.1rem', md: '1.4rem' }, lineHeight: 1.2 }}>
          Rendimiento de Jueces
        </Typography>
        {campName && (
          <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.85rem', mt: 0.5 }}>
            {campName}
          </Typography>
        )}
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, px: { xs: 2, md: 4 }, py: 3, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <TextField
          size="small"
          placeholder="Buscar por nombre o usuario..."
          value={searchInput}
          onChange={handleSearchChange}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'text.disabled' }} /></InputAdornment> }}
          sx={{ mb: 3, maxWidth: 400, flexShrink: 0, backgroundColor: '#fff', borderRadius: 1 }}
        />

        <Grid container spacing={3} sx={{ flex: 1, minHeight: 0 }}>
          {/* Left: judge list */}
          <Grid item xs={12} md={4} lg={3} sx={{ height: { md: 'calc(100vh - 180px)' } }}>
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1.5, fontWeight: 600, flexShrink: 0 }}>
                {items.length} jueces{hasMore ? '+' : ''}
              </Typography>
              <Box sx={{ flex: 1, overflowY: 'auto' }}>
                {items.map(item => (
                  <JudgeListItem
                    key={item.judge._id}
                    data={item}
                    selected={selectedJudge?.judge._id === item.judge._id}
                    onClick={() => setSelectedJudge(item)}
                  />
                ))}
                <Box ref={sentinelRef} sx={{ height: 4 }} />
                {loading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={22} sx={{ color: '#284B32' }} />
                  </Box>
                )}
                {!loading && items.length === 0 && (
                  <Typography color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
                    {search ? 'Sin resultados.' : 'Sin jueces disponibles.'}
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>

          {/* Right: detail */}
          <Grid item xs={12} md={8} lg={9} sx={{ height: { md: 'calc(100vh - 180px)' } }}>
            <Card sx={{ height: '100%', overflow: 'auto' }}>
              {selectedJudge ? (
                <JudgeDetail data={selectedJudge} />
              ) : !loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Typography color="text.disabled">Selecciona un juez para ver el detalle.</Typography>
                </Box>
              ) : null}
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default PublicJudgePerformancePage;
